import type { Point, Stroke, Tool } from '@blackboard/shared';
import { strokePath, pathToPath2D } from './freehand';
import { getChalkPattern } from './chalkTexture';

export interface StrokeRenderInput {
  tool: Tool;
  color: string;
  size: number;
  points: Point[];
}

/**
 * Outline cache: perfect-freehand's getStroke + Path2D build is the hottest
 * CPU cost when repainting committed strokes. Keyed on the points array
 * reference — committed strokes never mutate theirs, so they hit forever;
 * a growing live stroke only rebuilds while its length changes.
 */
const outlineCache = new WeakMap<
  Point[],
  { len: number; size: number; simulate: boolean; path: Path2D }
>();

function cachedOutline(points: Point[], size: number, simulate: boolean): Path2D | null {
  const hit = outlineCache.get(points);
  if (hit && hit.len === points.length && hit.size === size && hit.simulate === simulate) {
    return hit.path;
  }
  const outline = strokePath(points, { size, simulatePressure: simulate });
  if (outline.length < 3) return null;
  const path = pathToPath2D(outline);
  outlineCache.set(points, { len: points.length, size, simulate, path });
  return path;
}

export function renderStroke(ctx: CanvasRenderingContext2D, input: StrokeRenderInput) {
  if (input.points.length === 0) return;
  const simulate = input.points.every((p) => p.pressure === 0.5);
  const path = cachedOutline(input.points, input.size, simulate);
  if (!path) return;

  ctx.save();
  if (input.tool === 'chalk') {
    ctx.fillStyle = input.color;
    ctx.globalAlpha = 0.92;
    ctx.fill(path);
    ctx.globalAlpha = 0.38;
    ctx.globalCompositeOperation = 'source-atop';
    ctx.fillStyle = getChalkPattern(ctx);
    ctx.fill(path);
  } else {
    ctx.shadowColor = 'rgba(0,0,0,0.12)';
    ctx.shadowBlur = 1;
    ctx.fillStyle = input.color;
    ctx.fill(path);
  }
  ctx.restore();
}

export function renderCommittedStroke(
  ctx: CanvasRenderingContext2D,
  s: Stroke,
  sizePxMap: (sizeIdx: number, tool: Tool) => number
) {
  const px = sizePxMap(s.size, s.tool);
  renderStroke(ctx, { tool: s.tool, color: s.color, size: px, points: s.points });
}
